import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';
import { celebrateAdd } from '../lib/celebrate';

const KEY = 'dealgo_shopping_list';
const ShoppingListContext = createContext(null);
const readLocal = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; } };
const writeLocal = (items) => localStorage.setItem(KEY, JSON.stringify(items));
const uid = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const strip = (p) => ({ catalog_id: p.catalog_id, page: p.page, name: p.name, price: p.price || '', box: p.box || null, brand: p.brand || '', brand_id: p.brand_id || null, image: p.image || null, checked: !!p.checked });
const same = (a, b) => a.catalog_id === b.catalog_id && a.page === b.page && a.name.trim().toLowerCase() === b.name.trim().toLowerCase();

export const ShoppingListProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState(readLocal);
  const loggedIn = !!user;

  useEffect(() => {
    if (user === undefined) return;
    if (!user) { setItems(readLocal()); return; }
    const pending = readLocal();
    const req = pending.length ? api.post('/auth/me/shopping-list/merge', { items: pending.map(strip) }) : api.get('/auth/me/shopping-list');
    req.then((r) => { setItems(r.data.items); if (pending.length) localStorage.removeItem(KEY); }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLocal = (next) => { writeLocal(next); setItems(next); };

  const add = useCallback(async (p) => {
    if (loggedIn) { const { data } = await api.post('/auth/me/shopping-list', strip(p)); setItems(data.items); celebrateAdd(p.image); return; }
    setItems((cur) => { if (cur.some((i) => same(i, p))) return cur; const next = [...cur, { id: uid(), ...strip(p), added_at: new Date().toISOString() }]; writeLocal(next); return next; });
    celebrateAdd(p.image);
  }, [loggedIn]);

  const remove = useCallback(async (id) => {
    if (loggedIn) { const { data } = await api.delete(`/auth/me/shopping-list/${id}`); setItems(data.items); return; }
    setItems((cur) => { const next = cur.filter((i) => i.id !== id); writeLocal(next); return next; });
  }, [loggedIn]);

  const toggle = useCallback(async (id, checked) => {
    if (loggedIn) { const { data } = await api.put(`/auth/me/shopping-list/${id}`, { checked }); setItems(data.items); return; }
    setItems((cur) => { const next = cur.map((i) => (i.id === id ? { ...i, checked } : i)); writeLocal(next); return next; });
  }, [loggedIn]);

  const clearChecked = useCallback(async () => {
    if (loggedIn) { const { data } = await api.delete('/auth/me/shopping-list?checked_only=true'); setItems(data.items); return; }
    setLocal(items.filter((i) => !i.checked));
  }, [loggedIn, items]);

  const refresh = useCallback(async () => {
    if (loggedIn) { const { data } = await api.get('/auth/me/shopping-list'); setItems(data.items); return; }
    const local = readLocal();
    if (!local.length) return;
    const { data } = await api.post('/shopping-list/enrich', { items: local.map(strip) });
    setItems(local.map((i, n) => ({ ...i, ...(data.items[n] || {}), id: i.id })));
  }, [loggedIn]);

  const has = useCallback((p) => items.some((i) => same(i, p)), [items]);
  const value = { items, count: items.length, add, remove, toggle, clearChecked, refresh, has };
  return <ShoppingListContext.Provider value={value}>{children}</ShoppingListContext.Provider>;
};

export const useShoppingList = () => useContext(ShoppingListContext);
